import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, Wallet, FileText, Landmark, Percent, Upload,
  BarChart3, Lock, Users2, Sparkles, Layers, Ticket, AlertTriangle,
} from "lucide-react";
import ExpensesPanel from "./ExpensesPanel";
import SuppliersPanel from "./SuppliersPanel";
import TasasPanel from "./TasasPanel";
import PendingSalesPanel from "./PendingSalesPanel";
import { IncomesPanel, BankPanel, VatPanel } from "./AccountingPhase2Panels";
import { usePinSecurity } from "@/contexts/PinContext";
import { useAccountingSections } from "@/lib/accounting-section";

// V1.36 · Contabilidad — Módulo real (Gastos + Proveedores + Reportes).
// Arquitectura preparada para Ingresos / Bancos / IVA / Exportaciones.

const SUBTABS = [
  { key: "gastos",       label: "Gastos",         icon: Wallet,   enabled: true },
  { key: "proveedores",  label: "Proveedores",    icon: Users2,   enabled: true },
  { key: "ingresos",     label: "Ingresos",       icon: FileText, enabled: true },
  { key: "ventas-pend",  label: "Ventas pendientes", icon: AlertTriangle, enabled: true },
  { key: "iva",          label: "IVA",            icon: Percent,  enabled: true },
  { key: "bancos",       label: "Bancos",         icon: Landmark, enabled: true },
  { key: "reportes",     label: "Reportes",       icon: BarChart3, enabled: true },
  { key: "tasas",        label: "Tasas",          icon: Ticket,   enabled: true },
  { key: "exportaciones", label: "Exportaciones", icon: Upload,   enabled: false, soon: "V1.40" },
];

export default function ContabilidadTab() {
  const [subtab, setSubtab] = useState("gastos");
  const [counter, setCounter] = useState(305);
  const { sections, active, activeId, setActiveId } = useAccountingSections();

  const refreshCounter = useCallback(async () => {
    try {
      const { data } = await api.get("/accounting/counter");
      setCounter(data?.value ?? 305);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { refreshCounter(); }, [refreshCounter]);

  return (
    <div className="space-y-4 max-w-[1440px] mx-auto" data-testid="contabilidad-tab">
      {/* Cabecera */}
      <div className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-orange-50 to-amber-50 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-500 p-2.5 text-white">
              <Calculator className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight">Contabilidad</h2>
                <span className="text-[10px] font-mono text-neutral-500">V1.38</span>
              </div>
              <p className="text-sm text-neutral-700 mt-1 leading-snug">
                Multi-sección · Foto → OCR → PDF → OneDrive · <b>&lt;15 s por gasto</b>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* V1.38 · Selector de sección contable activa */}
            <SectionSelector sections={sections} activeId={activeId} onChange={setActiveId} />
            {/* Contador correlativo */}
            <CounterCard value={counter} onChanged={refreshCounter} />
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 border-b border-neutral-200">
        {SUBTABS.map((s) => {
          const Icon = s.icon;
          const active = subtab === s.key;
          const disabled = !s.enabled;
          return (
            <button
              key={s.key}
              onClick={() => s.enabled && setSubtab(s.key)}
              disabled={disabled}
              data-testid={`contabilidad-subtab-${s.key}`}
              className={
                "px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 transition-colors " +
                (active
                  ? "border-orange-500 text-orange-700"
                  : disabled
                  ? "border-transparent text-neutral-400 cursor-not-allowed"
                  : "border-transparent text-neutral-600 hover:text-neutral-900")
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {s.label}
              {s.soon && (
                <span className="text-[9px] uppercase tracking-wider bg-neutral-200 text-neutral-500 px-1 rounded">
                  {s.soon}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenido */}
      {subtab === "gastos" && <ExpensesPanel sectionId={activeId} sectionLabel={active ? `${active.code} · ${active.name}` : ""} />}
      {subtab === "proveedores" && <SuppliersPanel sectionId={activeId} />}
      {subtab === "ingresos" && <IncomesPanel sectionId={activeId} sectionLabel={active ? `${active.code} · ${active.name}` : ""} />}
      {subtab === "ventas-pend" && <PendingSalesPanel />}
      {subtab === "iva" && <VatPanel sectionId={activeId} sectionLabel={active ? `${active.code} · ${active.name}` : ""} />}
      {subtab === "bancos" && <BankPanel sectionId={activeId} sectionLabel={active ? `${active.code} · ${active.name}` : ""} />}
      {subtab === "reportes" && <ReportsPanel sectionId={activeId} sectionLabel={active ? `${active.code} · ${active.name}` : ""} />}
      {subtab === "tasas" && <TasasPanel />}
    </div>
  );
}

function SectionSelector({ sections, activeId, onChange }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-white px-3 py-2 shadow-sm">
      <Layers className="h-4 w-4 text-orange-600" />
      <div className="flex flex-col leading-tight">
        <span className="text-[9px] uppercase tracking-wider text-neutral-500 font-semibold">Sección activa</span>
        <select
          data-testid="contab-section-selector"
          value={activeId}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent text-sm font-bold text-neutral-800 focus:outline-none min-w-[10rem] cursor-pointer"
        >
          {sections.length === 0 && <option value="">Cargando…</option>}
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} · {s.name}{s.active === false ? " (inactiva)" : ""}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function CounterCard({ value, onChanged }) {
  const pin = usePinSecurity();
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(String(value));

  useEffect(() => { setNext(String(value)); }, [value]);

  const save = async () => {
    const n = parseInt(next, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error("Valor inválido"); return; }
    try {
      await pin.require("accounting.counter.update");
      await api.patch("/accounting/counter", { value: n });
      toast.success(`Contador ajustado: siguiente será #${n + 1}`);
      setEditing(false);
      onChanged?.();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  return (
    <div className="rounded-xl border border-orange-200 bg-white/70 px-3 py-2 shrink-0"
         data-testid="expense-counter-card">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1">
        <Lock className="h-3 w-3" />
        Contador · Próximo
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-20 h-8 rounded border border-neutral-300 px-2 text-lg font-bold tabular-nums text-orange-700"
            data-testid="expense-counter-input"
          />
          <button onClick={save} className="text-xs px-2 py-1 rounded bg-orange-500 text-white"
                  data-testid="expense-counter-save">Guardar</button>
          <button onClick={() => setEditing(false)}
                  className="text-xs px-2 py-1 rounded text-neutral-500 hover:bg-neutral-100">✕</button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold tabular-nums text-orange-700"
               data-testid="expense-counter-value">
            #{value + 1}
          </div>
          <button onClick={() => setEditing(true)}
                  className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold hover:text-orange-700"
                  data-testid="expense-counter-edit">
            Editar (PIN)
          </button>
        </div>
      )}
    </div>
  );
}

function ReportsPanel({ sectionId, sectionLabel }) {
  const [tab, setTab] = useState("by-supplier");
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { year };
        if (quarter) params.quarter = quarter;
        if (sectionId) params.section_id = sectionId;
        const { data: r } = await api.get(`/accounting/reports/${tab}`, { params });
        setData(r);
      } catch (err) {
        setData({ rows: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [tab, year, quarter, sectionId]);

  const rows = tab === "by-account"
    ? (data?.by_main || [])
    : tab === "vat-summary"
    ? (data?.rows || [])
    : (data?.rows || []);

  return (
    <div className="space-y-3" data-testid="reports-panel">
      {sectionLabel && (
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          <Layers className="h-3.5 w-3.5 text-orange-600" />
          <span className="font-mono">Reportes filtrados por</span>
          <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">{sectionLabel}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {[
            { k: "by-supplier", l: "Por proveedor" },
            { k: "by-account",  l: "Por cuenta/tipo" },
            { k: "by-quarter",  l: "Por trimestre" },
            { k: "vat-summary", l: "IVA soportado" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`report-tab-${t.k}`}
              className={
                "text-xs px-2.5 py-1.5 rounded-full border transition-colors " +
                (tab === t.k
                  ? "border-orange-500 bg-orange-50 text-orange-700 font-bold"
                  : "border-neutral-200 bg-white hover:border-orange-300 text-neutral-600")
              }
            >
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="h-9 rounded border border-neutral-300 px-2 text-sm">
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={quarter} onChange={(e) => setQuarter(e.target.value)}
                className="h-9 rounded border border-neutral-300 px-2 text-sm">
          <option value="">Todo el año</option>
          <option value="1">T1</option><option value="2">T2</option>
          <option value="3">T3</option><option value="4">T4</option>
        </select>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-sm text-neutral-500">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-neutral-500">Sin datos para este filtro</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">
                  {tab === "by-supplier" ? "Proveedor" :
                   tab === "by-account"  ? "Cuenta" :
                   tab === "by-quarter"  ? "Trimestre" :
                   "IVA %"}
                </th>
                <th className="text-right px-3 py-2">Nº</th>
                <th className="text-right px-3 py-2">Base</th>
                <th className="text-right px-3 py-2">IVA</th>
                <th className="text-right px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="px-3 py-2 font-medium">
                    {tab === "by-quarter" ? `T${r.key}` :
                     tab === "vat-summary" ? `${r.vat_percent}%` : r.key || "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmt(r.base_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmt(r.vat_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmt(r.total_amount)}</td>
                </tr>
              ))}
            </tbody>
            {tab === "vat-summary" && data?.totals && (
              <tfoot>
                <tr className="border-t-2 border-orange-500 bg-orange-50 font-bold">
                  <td colSpan={2} className="px-3 py-2 text-right">TOTAL</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(data.totals.base_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(data.totals.vat_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(data.totals.total_amount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
      {tab === "by-account" && data?.by_kind && data.by_kind.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
            Desglose por tipo de gasto
          </div>
          <table className="w-full text-sm">
            <tbody>
              {data.by_kind.map((r, i) => (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="px-3 py-2">{r.key}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmt(r.base_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmt(r.vat_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmt(r.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmt(v) { return `${Number(v || 0).toFixed(2)} €`; }

// Silence unused-import warnings — kept in case future sub-tabs need them.
void Sparkles;
